import { Component, OnInit } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { Firestore } from '@angular/fire/firestore'
import {
    AffiliateBountyStatus,
    AffiliateCandidateOutcome,
    AffiliateCandidateOutcomeLabels,
    AffiliateDashboard,
    AffiliateKind,
    AffiliateKindLabels,
    AffiliateReferralType,
    AffiliateReferralView,
    AffiliateStatus,
} from '@parkupp/core'
import { ToastrService } from 'ngx-toastr'
import { AffiliateBountyRow, AffiliateService } from '../../../services/affiliate.service'

type Detail = AffiliateDashboard & { clicksByDay: { day: string; clicks: number }[]; commissionsRaw: any[]; bounties?: AffiliateBountyRow[] }

@Component({
    selector: 'app-admin-affiliate-detail',
    templateUrl: './admin-affiliate-detail.component.html',
    styleUrls: ['./admin-affiliates.component.scss'],
})
export class AdminAffiliateDetailComponent implements OnInit {
    uid: string
    detail: Detail | null = null
    loading = true
    saving = false
    statuses = Object.values(AffiliateStatus)
    settings = {
        status: AffiliateStatus.ACTIVE as AffiliateStatus,
        kind: AffiliateKind.PARKER_PROMOTER as AffiliateKind,
        parkerCommissionPct: 0,
        landlordCommissionPct: 0,
        parkerWindowMonths: 12,
        landlordWindowMonths: 12,
        notes: '',
        // Security guards
        displayName: '',
        phone: '',
        guardEmail: '',
        bountyAmount: 0,
        bountyPaidInvoices: 3,
    }
    Kind = AffiliateKind
    BountyStatus = AffiliateBountyStatus
    promoterKinds = [AffiliateKind.PARKER_PROMOTER, AffiliateKind.LANDLORD_PROMOTER]
    links = { parker: '', landlord: '' }
    /** Which link the Sharing options pop-up is open for */
    shareLink: 'parker' | 'landlord' | null = null
    clickDays: { day: string; clicks: number }[] = []
    maxClicks = 1

    // Attach parker
    showAttach = false
    attachEmail = ''
    attachFound: { uid: string; name: string } | null = null
    attachNotes = ''
    attachReplace = false

    ReferralType = AffiliateReferralType
    Outcome = AffiliateCandidateOutcome
    outcomeLabel(outcome: string): string {
        return AffiliateCandidateOutcomeLabels[outcome as AffiliateCandidateOutcome] || outcome
    }

    constructor(private route: ActivatedRoute, private affiliateService: AffiliateService, private firestore: Firestore, private toastr: ToastrService) {
        this.uid = this.route.snapshot.params['uid']
    }

    ngOnInit(): void {
        this.load()
    }

    async load() {
        this.loading = true
        try {
            this.detail = await this.affiliateService.adminDetail(this.uid)
            const a: any = this.detail.affiliate
            this.settings = {
                status: a.status,
                kind: a.kind || AffiliateKind.PARKER_PROMOTER,
                parkerCommissionPct: a.parkerCommissionPct,
                landlordCommissionPct: a.landlordCommissionPct,
                parkerWindowMonths: a.parkerWindowMonths,
                landlordWindowMonths: a.landlordWindowMonths,
                notes: a.notes || '',
                displayName: a.displayName || '',
                phone: a.phone || '',
                guardEmail: a.email || '',
                bountyAmount: a.bountyAmount ?? 0,
                bountyPaidInvoices: a.bountyPaidInvoices ?? 3,
            }
            this.links = this.affiliateService.links(a.code)
            this.buildClickDays()
        } catch (error: any) {
            this.toastr.error(error?.message || 'Could not load the affiliate')
        } finally {
            this.loading = false
        }
    }

    /** Last 90 days, zero-filled, for the bar chart. */
    private buildClickDays() {
        const byDay = new Map((this.detail?.clicksByDay || []).map((c) => [c.day, c.clicks]))
        this.clickDays = Array.from({ length: 90 }, (_, i) => {
            const day = new Date(Date.now() - (89 - i) * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
            return { day, clicks: byDay.get(day) || 0 }
        })
        this.maxClicks = Math.max(1, ...this.clickDays.map((d) => d.clicks))
    }

    get clicks90(): number {
        return this.clickDays.reduce((sum, d) => sum + d.clicks, 0)
    }

    async saveSettings() {
        this.saving = true
        try {
            const st = this.settings
            await this.affiliateService.adminSave(
                this.isGuard
                    ? {
                          uid: this.uid,
                          status: st.status,
                          notes: st.notes,
                          displayName: st.displayName,
                          phone: st.phone,
                          guardEmail: st.guardEmail,
                          bountyAmount: st.bountyAmount,
                          bountyPaidInvoices: st.bountyPaidInvoices,
                      }
                    : {
                          uid: this.uid,
                          status: st.status,
                          kind: st.kind,
                          notes: st.notes,
                          parkerCommissionPct: st.parkerCommissionPct,
                          landlordCommissionPct: st.landlordCommissionPct,
                          parkerWindowMonths: st.parkerWindowMonths,
                          landlordWindowMonths: st.landlordWindowMonths,
                      }
            )
            this.toastr.success('Saved. New rates apply to payments from now on.')
            await this.load()
        } catch (error: any) {
            this.toastr.error(error?.message || 'Could not save')
        } finally {
            this.saving = false
        }
    }

    get isGuard(): boolean {
        return (this.detail?.affiliate as any)?.kind === AffiliateKind.SECURITY_GUARD
    }

    kindLabel(kind: AffiliateKind | undefined): string {
        return AffiliateKindLabels[kind || AffiliateKind.PARKER_PROMOTER]
    }

    bountyTotal(status: AffiliateBountyStatus): number {
        return (this.detail?.bounties || []).filter((b) => b.status === status).reduce((sum, b) => sum + b.amount, 0)
    }

    async copy(value: string) {
        await navigator.clipboard.writeText(value)
        this.toastr.success('Copied')
    }

    mine(c: any) {
        return (c.candidates || []).find((x: any) => x.affiliateUid === this.uid) || {}
    }

    others(c: any) {
        return (c.candidates || []).filter((x: any) => x.affiliateUid !== this.uid)
    }

    async revoke(r: AffiliateReferralView) {
        const notes = prompt(`Remove the referral for ${r.name}? Future payments won't earn commission. Reason:`)
        if (notes === null) return
        try {
            await this.affiliateService.adminRevokeReferral(r.id, notes)
            this.toastr.success('Referral removed')
            await this.load()
        } catch (error: any) {
            this.toastr.error(error?.message || 'Could not remove the referral')
        }
    }

    async findParker() {
        const email = this.attachEmail.trim()
        if (!email) return
        const match = await this.affiliateService.findAccountByEmail(email)
        if (!match) {
            this.attachFound = null
            this.toastr.warning('No ParkUpp user with that email')
            return
        }
        this.attachFound = { uid: match.uid, name: match.name }
        this.attachReplace = !!match.data.affiliateUid
    }

    async attachParker() {
        if (!this.attachFound) return
        this.saving = true
        try {
            await this.affiliateService.adminAttachReferral({
                type: AffiliateReferralType.PARKER,
                affiliateUid: this.uid,
                referredUid: this.attachFound.uid,
                referredEmail: this.attachEmail.trim(),
                notes: this.attachNotes,
                replace: this.attachReplace,
            })
            this.toastr.success('Parker referral added')
            this.showAttach = false
            this.attachFound = null
            this.attachEmail = ''
            this.attachNotes = ''
            await this.load()
        } catch (error: any) {
            this.toastr.error(error?.message || 'Could not add the referral')
        } finally {
            this.saving = false
        }
    }
}
